/**
 * ComparativeAnalytics Module
 * Interactive asset comparison with multiple view modes and real API integration
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import PageContainer from '@/components/layout/PageContainer'
import { Card, MetricCard } from '@/components/common'
import { ContextualNarrativePanel, ProactiveInsightsPanel, DecisionSupportPanel } from '@/components/insights'
import { FilterPanel } from '@/components/filters'
import { useComparisonStore, useAssetStore } from '@/store'
import ComparisonHeader from './ComparisonHeader'
import ComparisonTable from './ComparisonTable'
import SideBySideView from './SideBySideView'
import OverlayView from './OverlayView'
// narrativeGenerator replaced with inline useMemo narratives
import { assetApi } from '@/services/assetApi'

export default function ComparativeAnalytics() {
  const navigate = useNavigate()
  const { assets, fetchAssets } = useAssetStore()
  const {
    selectedAssetIds,
    kpiComparison,
    comparisonMode,
    loading,
    usingMockData,
    fetchKPIComparison,
    fetchTrendComparison,
    fetchCorrelations,
    setSelectedAssets
  } = useComparisonStore()

  // Guard against assets not being an array
  const safeAssets = Array.isArray(assets) ? assets : []

  // Track if initial auto-select has been done
  const [autoSelectDone, setAutoSelectDone] = useState(false)

  // Model performance for AI confidence
  const [modelPerformance, setModelPerformance] = useState<{ accuracy: number }>({ accuracy: 0.91 })

  // Fetch model performance
  const fetchModelPerformance = useCallback(async () => {
    try {
      const result = await assetApi.getFleetModelPerformance()
      setModelPerformance({ accuracy: result.data.accuracy })
    } catch (error) {
      console.warn('Failed to fetch model performance:', error)
    }
  }, [])

  // Fetch assets on mount if not loaded
  useEffect(() => {
    if (safeAssets.length === 0) {
      fetchAssets()
    }
  }, [safeAssets.length, fetchAssets])

  // Fetch model performance on mount
  useEffect(() => {
    fetchModelPerformance()
  }, [fetchModelPerformance])

  // Auto-select first 3 assets when they load (if none are selected)
  useEffect(() => {
    if (!autoSelectDone && safeAssets.length >= 2 && selectedAssetIds.length === 0) {
      // Select first 3 assets (or 2 if only 2 available)
      const assetsToSelect = safeAssets.slice(0, Math.min(3, safeAssets.length)).map(a => a.id)
      setSelectedAssets(assetsToSelect)
      setAutoSelectDone(true)
    }
  }, [safeAssets, selectedAssetIds.length, autoSelectDone, setSelectedAssets])

  // Auto-fetch comparison data when assets are selected
  useEffect(() => {
    if (selectedAssetIds.length > 0) {
      fetchKPIComparison()
      fetchTrendComparison()
      fetchCorrelations()
    }
  }, [selectedAssetIds, fetchKPIComparison, fetchTrendComparison, fetchCorrelations])

  const handleRefresh = async () => {
    await Promise.all([
      fetchKPIComparison(),
      fetchTrendComparison(7),
      fetchCorrelations()
    ])
  }

  // Calculate summary metrics with comprehensive null safety
  const summaryMetrics = useMemo(() => {
    if (!kpiComparison?.assets || kpiComparison.assets.length === 0) {
      return {
        avgReliability: '--',
        bestPerformer: '--',
        worstPerformer: '--',
        avgMTBF: '--',
        avgMTTR: '--'
      }
    }

    // Safe extraction with fallbacks for missing metrics
    const reliabilities = kpiComparison.assets.map(a => a?.metrics?.reliability ?? 0)
    const mtbfs = kpiComparison.assets.map(a => a?.metrics?.mtbf ?? 0)
    const mttrs = kpiComparison.assets.map(a => a?.metrics?.mttr ?? 0)

    const avgRel = reliabilities.length > 0 ? reliabilities.reduce((a, b) => a + b, 0) / reliabilities.length : 0
    const avgMtbf = mtbfs.length > 0 ? mtbfs.reduce((a, b) => a + b, 0) / mtbfs.length : 0
    const avgMttr = mttrs.length > 0 ? mttrs.reduce((a, b) => a + b, 0) / mttrs.length : 0

    return {
      avgReliability: avgRel > 0 ? `${avgRel.toFixed(1)}%` : '--',
      bestPerformer: kpiComparison.best_performer || '--',
      worstPerformer: kpiComparison.worst_performer || '--',
      avgMTBF: avgMtbf > 0 ? `${Math.round(avgMtbf)}h` : '--',
      avgMTTR: avgMttr > 0 ? `${avgMttr.toFixed(1)}h` : '--'
    }
  }, [kpiComparison])

  // Generate comprehensive AI narrative with actionable insights
  const analysisNarrative = useMemo(() => {
    if (!kpiComparison?.assets || kpiComparison.assets.length < 2) {
      return `Select 2 or more assets to generate comparison insights.

The AI analysis will include:
• Performance ranking and benchmarking
• Best practices from top performers
• Areas for improvement in underperformers
• Correlation analysis between KPIs
• Actionable recommendations`
    }

    const assets = kpiComparison.assets
    const fleetAvg = kpiComparison.fleet_average || {}
    const bestAsset = assets.find(a => a.asset_id === kpiComparison.best_performer)
    const worstAsset = assets.find(a => a.asset_id === kpiComparison.worst_performer)

    // Calculate performance gaps
    const reliabilitySpread = Math.max(...assets.map(a => a.metrics?.reliability || 0)) -
                              Math.min(...assets.map(a => a.metrics?.reliability || 0))
    const mtbfSpread = Math.max(...assets.map(a => a.metrics?.mtbf || 0)) -
                       Math.min(...assets.map(a => a.metrics?.mtbf || 0))
    const criticalAssets = assets.filter(a => a.status === 'critical')
    const warningAssets = assets.filter(a => a.status === 'warning')

    // Calculate fleet risk score
    const fleetRiskScore = assets.length > 0
      ? Math.round((criticalAssets.length * 30 + warningAssets.length * 15) / assets.length)
      : 0

    // Build comprehensive narrative
    let narrative = `**Comparative Fleet Analysis** | ${assets.length} Assets Analyzed\n\n`

    // Performance Overview
    narrative += `**Performance Overview:**\n`
    narrative += `Fleet average reliability: ${(fleetAvg.reliability || 0).toFixed(1)}% | `
    narrative += `MTBF: ${Math.round(fleetAvg.mtbf || 0)}h | MTTR: ${(fleetAvg.mttr || 0).toFixed(1)}h\n`
    narrative += `Performance spread: ${reliabilitySpread.toFixed(1)}% reliability gap, ${Math.round(mtbfSpread)}h MTBF variance\n\n`

    // Risk Assessment
    if (criticalAssets.length > 0 || warningAssets.length > 0) {
      narrative += `**⚠️ Immediate Attention Required:**\n`
      if (criticalAssets.length > 0) {
        narrative += `• ${criticalAssets.length} asset(s) in critical state: ${criticalAssets.map(a => a.asset_id).join(', ')}\n`
        narrative += `  Risk: Potential unplanned downtime. Estimated impact: 4-8 hours production loss per incident\n`
      }
      if (warningAssets.length > 0) {
        narrative += `• ${warningAssets.length} asset(s) showing degradation: ${warningAssets.map(a => a.asset_id).join(', ')}\n`
        narrative += `  Risk: May escalate to critical within 7-14 days if unaddressed\n`
      }
      narrative += '\n'
    }

    // Best Performer Analysis
    if (bestAsset) {
      narrative += `**🏆 Top Performer: ${bestAsset.asset_id}**\n`
      narrative += `Reliability: ${(bestAsset.metrics?.reliability || 0).toFixed(1)}% | `
      narrative += `MTBF: ${Math.round(bestAsset.metrics?.mtbf || 0)}h | `
      narrative += `Anomaly Rate: ${(bestAsset.metrics?.anomaly_rate || 0).toFixed(2)}%\n`
      narrative += `Key differentiators: Lower vibration variance, consistent operating temperatures, optimized maintenance intervals\n\n`
    }

    // Underperformer Analysis with Technical Facts
    if (worstAsset) {
      const reliabilityGap = (bestAsset?.metrics?.reliability || 0) - (worstAsset.metrics?.reliability || 0)
      const mtbfGap = (bestAsset?.metrics?.mtbf || 0) - (worstAsset.metrics?.mtbf || 0)

      narrative += `**🔧 Requires Attention: ${worstAsset.asset_id}**\n`
      narrative += `Reliability: ${(worstAsset.metrics?.reliability || 0).toFixed(1)}% (${reliabilityGap.toFixed(1)}% below best)\n`
      narrative += `MTBF: ${Math.round(worstAsset.metrics?.mtbf || 0)}h (${Math.round(mtbfGap)}h shorter cycles)\n`
      narrative += `Technical indicators: Higher anomaly frequency, elevated MTTR suggests complex failure modes\n\n`
    }

    // Protective Measures
    narrative += `**🛡️ Protective Measures:**\n`
    if (fleetRiskScore > 20) {
      narrative += `• Increase monitoring frequency for critical assets to 15-minute intervals\n`
      narrative += `• Pre-position spare parts for high-risk components (bearings, seals, sensors)\n`
    }
    narrative += `• Apply operational parameters from ${kpiComparison.best_performer || 'top performer'} as baseline\n`
    narrative += `• Schedule proactive maintenance during low-demand windows to minimize impact\n\n`

    // Recommended Actions
    narrative += `**📋 Priority Actions:**\n`
    if (criticalAssets.length > 0) {
      narrative += `1. [URGENT] Conduct root cause analysis on ${criticalAssets[0].asset_id} - Focus on bearing condition and vibration patterns\n`
    }
    if (reliabilitySpread > 10) {
      narrative += `2. Standardize operating procedures to reduce ${reliabilitySpread.toFixed(0)}% reliability variance\n`
    }
    if (worstAsset && (worstAsset.metrics?.mttr || 0) > (fleetAvg.mttr || 0)) {
      narrative += `3. Review maintenance workflows for ${worstAsset.asset_id} - MTTR ${((worstAsset.metrics?.mttr || 0) - (fleetAvg.mttr || 0)).toFixed(1)}h above fleet average\n`
    }

    return narrative
  }, [kpiComparison])

  // Note: generateAssetInsight is available for future use when asset-level narratives are needed in the UI

  // Generate decision rationale
  const generateDecisionRationale = useCallback((worstPerformer: string | null, bestPerformer: string | null) => {
    const worstAsset = kpiComparison?.assets.find(a => a.asset_id === worstPerformer)
    const bestAsset = kpiComparison?.assets.find(a => a.asset_id === bestPerformer)

    if (!worstAsset || !bestAsset) return {}

    const reliabilityImprovement = (bestAsset.metrics?.reliability || 0) - (worstAsset.metrics?.reliability || 0)
    const mtbfImprovement = (bestAsset.metrics?.mtbf || 0) - (worstAsset.metrics?.mtbf || 0)
    const estimatedSavings = Math.round(reliabilityImprovement * 500) // $500 per 1% reliability improvement

    return {
      investigate: `Detailed analysis of ${worstPerformer} can identify specific failure modes. Focus areas: vibration spectrum analysis, thermal imaging, and lubricant condition. Expected reliability improvement: ${reliabilityImprovement.toFixed(1)}% based on best performer benchmarks.`,
      standardize: `Replicating ${bestPerformer}'s operational parameters (load profiles, maintenance intervals, operating temperatures) across fleet could yield ${reliabilityImprovement.toFixed(1)}% average improvement. Estimated annual savings: $${estimatedSavings.toLocaleString()}.`,
      maintenance: `Adjusting preventive maintenance based on MTBF analysis could extend mean time between failures by ${Math.round(mtbfImprovement)}h. Recommended: Implement condition-based maintenance triggers using vibration and temperature thresholds.`
    }
  }, [kpiComparison])

  // Determine status based on metrics - with comprehensive null safety
  const getReliabilityStatus = () => {
    const avg = kpiComparison?.fleet_average?.reliability
    if (avg === undefined || avg === null) return 'neutral'
    return avg >= 90 ? 'healthy' : avg >= 80 ? 'warning' : 'critical'
  }

  const getMTTRStatus = () => {
    const avg = kpiComparison?.fleet_average?.mttr
    if (avg === undefined || avg === null) return 'neutral'
    return avg <= 4 ? 'healthy' : avg <= 8 ? 'warning' : 'critical'
  }

  return (
    <PageContainer
      title="Comparative Analytics"
      subtitle="Interactive asset comparison and fleet benchmarking"
    >
      <div className="grid grid-cols-12 gap-4">
        {/* Filter Panel */}
        <div className="col-span-12 lg:col-span-3">
          <FilterPanel
            showDatePicker={true}
            showKPISelector={true}
            onApply={handleRefresh}
          />
        </div>

        {/* Main Content */}
        <div className="col-span-12 lg:col-span-9">
          {/* Summary Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <MetricCard
              label="Avg Reliability"
              value={summaryMetrics.avgReliability}
              status={getReliabilityStatus()}
              trend={kpiComparison?.assets ? 'up' : undefined}
              trendValue={1.2}
            />
            <MetricCard
              label="Best Performer"
              value={summaryMetrics.bestPerformer}
              status="healthy"
            />
            <MetricCard
              label="Needs Attention"
              value={summaryMetrics.worstPerformer}
              status="critical"
            />
            <MetricCard
              label="Avg MTBF"
              value={summaryMetrics.avgMTBF}
              status="neutral"
            />
            <MetricCard
              label="Avg MTTR"
              value={summaryMetrics.avgMTTR}
              status={getMTTRStatus()}
              trend={kpiComparison?.assets ? 'down' : undefined}
              trendValue={0.5}
            />
          </div>

          {/* Comparison Header with View Mode Toggle */}
          <ComparisonHeader onRefresh={handleRefresh} />

          {/* Comparison Content based on mode */}
          <Card className="min-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-surface-400">Loading comparison data...</span>
                </div>
              </div>
            ) : selectedAssetIds.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-lg font-medium text-surface-200 mb-2">
                  Select Assets to Compare
                </h3>
                <p className="text-surface-400 max-w-md">
                  Use the filter panel on the left to select 2-5 assets.
                  You can compare KPIs, view trends, and analyze performance side by side.
                </p>
              </div>
            ) : (
              <>
                {comparisonMode === 'table' && <ComparisonTable showSparklines={true} />}
                {comparisonMode === 'side-by-side' && <SideBySideView showTrends={true} />}
                {comparisonMode === 'overlay' && <OverlayView />}
              </>
            )}
          </Card>

          {/* Enhanced AI Insights */}
          {selectedAssetIds.length > 0 && kpiComparison?.assets && (
            <div className="mt-4 space-y-4">
              {/* Contextual Narrative Panel */}
              <ContextualNarrativePanel
                title="Comparative Analysis"
                summary={analysisNarrative}
                category="analysis"
                confidence={modelPerformance.accuracy}
                lastUpdated="Just now"
                insights={[
                  ...(kpiComparison.worst_performer ? [{
                    type: 'warning' as const,
                    title: `${kpiComparison.worst_performer} requires immediate attention`,
                    description: (() => {
                      const worstAsset = kpiComparison.assets.find(a => a.asset_id === kpiComparison.worst_performer)
                      const fleetAvg = kpiComparison.fleet_average || {}
                      const reliabilityGap = ((fleetAvg.reliability || 0) - (worstAsset?.metrics?.reliability || 0)).toFixed(1)
                      const anomalyRate = (worstAsset?.metrics?.anomaly_rate || 0).toFixed(2)
                      return `Reliability ${reliabilityGap}% below fleet average with ${anomalyRate}% anomaly rate. Technical indicators suggest potential bearing degradation or seal wear. Recommended: Vibration analysis and thermal imaging within 48 hours.`
                    })(),
                    metric: {
                      label: 'Reliability',
                      value: `${(kpiComparison.assets.find(a => a.asset_id === kpiComparison.worst_performer)?.metrics.reliability ?? 0).toFixed(1)}%`,
                      trend: 'down' as const
                    },
                    action: {
                      label: 'Investigate Root Cause',
                      onClick: () => navigate(`/assets/${kpiComparison.worst_performer}`)
                    }
                  }] : []),
                  ...(kpiComparison.best_performer ? [{
                    type: 'opportunity' as const,
                    title: `Benchmark from ${kpiComparison.best_performer}`,
                    description: (() => {
                      const bestAsset = kpiComparison.assets.find(a => a.asset_id === kpiComparison.best_performer)
                      const worstAsset = kpiComparison.assets.find(a => a.asset_id === kpiComparison.worst_performer)
                      const mtbfAdvantage = ((bestAsset?.metrics?.mtbf || 0) - (worstAsset?.metrics?.mtbf || 0)).toFixed(0)
                      return `Top performer with ${mtbfAdvantage}h longer MTBF than underperformers. Key success factors: Consistent operating parameters, optimized maintenance intervals, and proactive component replacement. Apply these practices fleet-wide for 8-15% reliability improvement.`
                    })(),
                    metric: {
                      label: 'Reliability',
                      value: `${(kpiComparison.assets.find(a => a.asset_id === kpiComparison.best_performer)?.metrics.reliability ?? 0).toFixed(1)}%`,
                      trend: 'up' as const
                    }
                  }] : []),
                  {
                    type: 'info' as const,
                    title: 'Fleet Performance Summary',
                    description: (() => {
                      const criticalCount = kpiComparison.assets.filter(a => a.status === 'critical').length
                      const warningCount = kpiComparison.assets.filter(a => a.status === 'warning').length
                      const healthyCount = kpiComparison.assets.filter(a => a.status === 'healthy').length
                      const avgReliability = (kpiComparison.fleet_average?.reliability ?? 0).toFixed(1)
                      return `Fleet reliability: ${avgReliability}% | Distribution: ${healthyCount} healthy, ${warningCount} warning, ${criticalCount} critical. ${criticalCount > 0 ? 'Protective measure: Increase monitoring frequency and pre-stage replacement parts.' : 'Protective measure: Maintain current preventive maintenance schedule.'}`
                    })()
                  }
                ]}
                decisions={[
                  {
                    id: 'investigate-worst',
                    label: 'Root Cause Analysis',
                    description: (() => {
                      const rationale = generateDecisionRationale(kpiComparison.worst_performer, kpiComparison.best_performer)
                      return rationale.investigate || `Conduct detailed inspection of ${kpiComparison.worst_performer} focusing on vibration analysis, thermal imaging, and lubricant condition.`
                    })(),
                    impact: 'high',
                    effort: 'medium',
                    recommended: true,
                    onClick: () => navigate(`/assets/${kpiComparison.worst_performer}`)
                  },
                  {
                    id: 'standardize-best',
                    label: 'Replicate Best Practices',
                    description: (() => {
                      const rationale = generateDecisionRationale(kpiComparison.worst_performer, kpiComparison.best_performer)
                      return rationale.standardize || `Apply operational procedures from ${kpiComparison.best_performer} including load profiles, maintenance intervals, and operating parameters.`
                    })(),
                    impact: 'high',
                    effort: 'high'
                  },
                  {
                    id: 'schedule-maintenance',
                    label: 'Condition-Based Maintenance',
                    description: (() => {
                      const rationale = generateDecisionRationale(kpiComparison.worst_performer, kpiComparison.best_performer)
                      return rationale.maintenance || 'Implement condition-based maintenance triggers using vibration and temperature thresholds to extend MTBF across fleet.'
                    })(),
                    impact: 'medium',
                    effort: 'low'
                  }
                ]}
              />

              {/* Proactive Insights */}
              <ProactiveInsightsPanel
                insights={[
                  ...(kpiComparison.assets.filter(a => a.status === 'critical').map(asset => {
                    const fleetAvg = kpiComparison.fleet_average || {}
                    const reliabilityDelta = ((fleetAvg.reliability || 0) - (asset.metrics?.reliability || 0)).toFixed(1)
                    const mtbfDelta = ((fleetAvg.mtbf || 0) - (asset.metrics?.mtbf || 0)).toFixed(0)
                    return {
                      id: `critical-${asset.asset_id}`,
                      priority: 'critical' as const,
                      category: 'anomaly' as const,
                      title: `Critical: ${asset.asset_id} requires immediate intervention`,
                      message: `Anomaly rate ${asset.metrics.anomaly_rate.toFixed(2)}% (${reliabilityDelta}% below fleet avg). Technical indicators: Elevated vibration signatures suggest bearing wear or shaft misalignment. MTBF ${mtbfDelta}h shorter than fleet average indicates accelerated degradation. Protective action: Isolate from high-load operations and conduct emergency diagnostics.`,
                      assetId: asset.asset_id,
                      timeframe: 'Immediate action required',
                      metrics: [
                        { label: 'Health', value: asset.metrics.health_score, unit: '%', change: -5 },
                        { label: 'MTTR', value: asset.metrics.mttr.toFixed(1), unit: 'h' },
                        { label: 'MTBF', value: Math.round(asset.metrics.mtbf), unit: 'h' }
                      ],
                      suggestedAction: {
                        label: 'Start Root Cause Analysis',
                        onClick: () => navigate(`/assets/${asset.asset_id}`)
                      },
                      dismissable: true
                    }
                  })),
                  ...(kpiComparison.assets.filter(a => a.status === 'warning').map(asset => {
                    const fleetAvg = kpiComparison.fleet_average || {}
                    const daysToFailure = Math.round(((asset.metrics?.reliability || 0) / 100) * 30)
                    return {
                      id: `warning-${asset.asset_id}`,
                      priority: 'high' as const,
                      category: 'trend' as const,
                      title: `Degradation trend: ${asset.asset_id}`,
                      message: `Performance declining over past 7 days. Current reliability ${asset.metrics.reliability.toFixed(1)}% vs fleet avg ${(fleetAvg.reliability || 0).toFixed(1)}%. Trend analysis projects potential critical threshold in ${daysToFailure} days if unaddressed. Protective measure: Schedule preventive maintenance within next maintenance window.`,
                      assetId: asset.asset_id,
                      timeframe: `Monitor: ${daysToFailure} days to threshold`,
                      metrics: [
                        { label: 'Reliability', value: asset.metrics.reliability.toFixed(1), unit: '%', change: -2 },
                        { label: 'Anomaly Rate', value: asset.metrics.anomaly_rate.toFixed(2), unit: '%' }
                      ],
                      suggestedAction: {
                        label: 'Schedule Inspection',
                        onClick: () => navigate(`/assets/${asset.asset_id}`)
                      },
                      dismissable: true
                    }
                  }))
                ]}
                maxVisible={3}
              />

              {/* Decision Support */}
              {kpiComparison.assets.some(a => a.status !== 'healthy') && (
                <DecisionSupportPanel
                  title="Priority Action Plan"
                  subtitle="AI-generated intervention roadmap with technical specifications"
                  recommendations={[
                    ...(kpiComparison.assets.filter(a => a.status === 'critical').map(asset => {
                      const fleetAvg = kpiComparison.fleet_average || {}
                      const reliabilityGap = Math.abs((fleetAvg.reliability || 0) - (asset.metrics?.reliability || 0))
                      const estimatedImprovement = Math.min(reliabilityGap, 20)
                      return {
                        id: `action-${asset.asset_id}`,
                        title: `Emergency Response: ${asset.asset_id}`,
                        description: `Critical condition with ${asset.metrics.anomaly_rate.toFixed(2)}% anomaly rate. Technical assessment indicates potential bearing degradation or seal failure based on vibration spectrum analysis. Immediate intervention prevents estimated 4-8 hour unplanned downtime.`,
                        impact: {
                          type: 'reliability' as const,
                          value: 'Reliability',
                          improvement: Math.round(estimatedImprovement)
                        },
                        effort: {
                          time: '2-4 hours',
                          complexity: 'medium' as const
                        },
                        priority: 'critical' as const,
                        assetId: asset.asset_id,
                        deadline: 'Within 24 hours',
                        steps: [
                          { id: '1', title: 'Vibration spectrum analysis', description: 'Capture and analyze frequency signatures to identify specific failure modes (bearing, gear, imbalance)', duration: '20 min' },
                          { id: '2', title: 'Thermal imaging scan', description: 'Identify hot spots indicating friction, misalignment, or electrical issues', duration: '15 min' },
                          { id: '3', title: 'Lubricant condition test', description: 'Check oil/grease for contamination, degradation, and particle count', duration: '30 min' },
                          { id: '4', title: 'Corrective maintenance', description: 'Replace worn components, realign shafts, or adjust operating parameters based on findings', duration: '1-2 hours' },
                          { id: '5', title: 'Verification run', description: 'Post-repair test at graduated load levels to confirm restoration', duration: '30 min' }
                        ],
                        onClick: () => navigate(`/assets/${asset.asset_id}`)
                      }
                    })),
                    ...(kpiComparison.assets.filter(a => a.status === 'warning').map(asset => ({
                      id: `preventive-${asset.asset_id}`,
                      title: `Preventive Action: ${asset.asset_id}`,
                      description: `Degradation trend detected. Proactive intervention now can prevent escalation to critical status and reduce repair costs by 60%.`,
                      impact: {
                        type: 'reliability' as const,
                        value: 'Reliability',
                        improvement: 8
                      },
                      effort: {
                        time: '1-2 hours',
                        complexity: 'low' as const
                      },
                      priority: 'high' as const,
                      assetId: asset.asset_id,
                      deadline: 'Within 7 days',
                      steps: [
                        { id: '1', title: 'Baseline measurement', description: 'Record current vibration, temperature, and performance metrics', duration: '15 min' },
                        { id: '2', title: 'Lubrication service', description: 'Apply appropriate lubricant per OEM specifications', duration: '20 min' },
                        { id: '3', title: 'Alignment verification', description: 'Check and correct shaft alignment if deviation >0.05mm', duration: '30 min' }
                      ],
                      onClick: () => navigate(`/assets/${asset.asset_id}`)
                    }))),
                    {
                      id: 'optimize-fleet',
                      title: 'Fleet Standardization Initiative',
                      description: (() => {
                        const bestAsset = kpiComparison.assets.find(a => a.asset_id === kpiComparison.best_performer)
                        const avgImprovement = ((bestAsset?.metrics?.reliability || 0) - (kpiComparison.fleet_average?.reliability || 0)).toFixed(1)
                        return `Apply ${kpiComparison.best_performer}'s operational parameters fleet-wide. Potential ${avgImprovement}% average reliability improvement. Key factors: Operating temperature range, load cycling patterns, and maintenance intervals.`
                      })(),
                      impact: {
                        type: 'performance' as const,
                        value: 'Fleet Performance',
                        improvement: 10
                      },
                      effort: {
                        time: '2-4 weeks',
                        complexity: 'high' as const
                      },
                      priority: 'medium' as const,
                      steps: [
                        { id: '1', title: 'Document top performer parameters', description: 'Capture operating temperatures, load profiles, maintenance schedules, and procedural differences' },
                        { id: '2', title: 'Gap analysis', description: 'Compare underperformers against benchmark to identify specific deviations' },
                        { id: '3', title: 'Create SOPs', description: 'Develop standardized operating procedures incorporating best practices' },
                        { id: '4', title: 'Phased rollout', description: 'Implement changes on critical assets first, monitor for 2 weeks, then expand' },
                        { id: '5', title: 'Measure impact', description: 'Track reliability and MTBF improvements to validate effectiveness' }
                      ]
                    }
                  ]}
                  showImpactSummary={true}
                />
              )}
            </div>
          )}

          {/* Getting Started Guide - when no assets selected */}
          {selectedAssetIds.length > 0 && !kpiComparison?.assets && !loading && (
            <div className="mt-4">
              <ContextualNarrativePanel
                title="Comparison Analysis"
                summary={`Select 2 or more assets to generate comparison insights.

The AI analysis will include:
• Performance ranking and benchmarking
• Best practices from top performers
• Areas for improvement in underperformers
• Correlation analysis between KPIs
• Actionable recommendations`}
                category="analysis"
                confidence={modelPerformance.accuracy * 0.55}
                insights={[]}
              />
            </div>
          )}

          {/* Data Source Indicator */}
          {usingMockData && selectedAssetIds.length > 0 && (
            <div className="mt-2 text-center">
              <span className="text-xs text-surface-500">
                Displaying demo data. Connect to API for live data.
              </span>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
